import { queryKeys, useHttpClient, useSession } from "@repo/api-client/react";
import {
  OrganizationSchema,
  type Organization,
  type OrganizationListItem,
  type UpdateOrganizationRequest,
} from "@repo/api-client/dtos";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebouncedAsyncCallback } from "../internal/useDebouncedAsyncCallback";
import { patchJSON } from "../internal/http";
import { syncOrgsFromCache } from "./sessionSync";

export type UpdateOrganizationVariables = {
  organizationId: string;
  updates: UpdateOrganizationRequest;
};

/**
 * Phase 9 (plan.md H5) — deep-merge for the two `jsonb`
 * sub-objects on `Organization`.
 *
 * Without this, an optimistic patch of `{ branding: { color: 'X' } }`
 * replaces the entire `branding` object in the cache for the
 * duration of the network round-trip. The live invoice preview
 * flickers, any other reader observed during the in-flight window
 * sees a half-populated branding object, and follow-up mutations
 * build off the partial state.
 *
 * Only `branding` and `contact` are deep-merged — every other
 * field on `Organization` is a scalar, so the shallow spread is
 * still correct for them. The function is exported so the rollback
 * in `onError` (and any peer hook that reuses this projection) can
 * use the identical merge.
 */
export function mergeOrganization(
  previous: Organization,
  patch: UpdateOrganizationRequest,
): Organization {
  const next: Organization = { ...previous, ...patch };
  if (patch.branding !== undefined) {
    next.branding =
      patch.branding === null
        ? null
        : {
            ...((previous.branding as Record<string, unknown> | null) ?? {}),
            ...(patch.branding as Record<string, unknown>),
          };
  }
  if (patch.contact !== undefined) {
    next.contact =
      patch.contact === null
        ? null
        : {
            ...((previous.contact as Record<string, unknown> | null) ?? {}),
            ...(patch.contact as Record<string, unknown>),
          };
  }
  return next;
}

/**
 * Patch an org. The cache is updated optimistically so settings
 * screens show the new value immediately, and rolled back on error.
 *
 * On success the list cache row is replaced as well (the list endpoint
 * doesn't return updated rows on its own and we want consistent
 * invalidation across the surface).
 */
export function useUpdateOrganization() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const { syncOrganizations } = useSession();

  const mutation = useMutation({
    mutationFn: ({ organizationId, updates }: UpdateOrganizationVariables) =>
      patchJSON(http, `/orgs/${organizationId}`, updates, OrganizationSchema),
    onMutate: async ({ organizationId, updates }) => {
      const detailKey = queryKeys.organizations.detail(organizationId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Organization>(detailKey);
      if (previous) {
        // Phase 9 (plan.md H5) — deep-merge the two nested jsonb
        // fields so an in-flight `{ branding: { color: 'X' } }`
        // doesn't blow away `branding.logoUrl` etc. for the
        // duration of the round-trip.
        queryClient.setQueryData<Organization>(
          detailKey,
          mergeOrganization(previous, updates),
        );
      }
      return { previous };
    },
    onError: (_err, { organizationId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.organizations.detail(organizationId),
          context.previous,
        );
      }
    },
    onSuccess: (organization) => {
      queryClient.setQueryData(
        queryKeys.organizations.detail(organization.id),
        organization,
      );
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.all(),
        (cache) =>
          cache
            ? {
                ...cache,
                items: cache.items.map((o) =>
                  o.id === organization.id ? { ...o, ...organization } : o,
                ),
              }
            : cache,
      );
      // Mirror into the session projection so the switcher / shell
      // pick up the new name / nickname / currency without waiting
      // for the next bootstrap.
      syncOrgsFromCache(queryClient, syncOrganizations);
    },
  });

  return {
    updateOrganization: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Debounced wrapper around `updateOrganization` for live-edit
 * surfaces (live invoice preview, future settings auto-save).
 *
 * Returns `{ call, flush, retry, pending, error, succeededAt }`:
 *   - `call(vars)` schedules the PATCH after `delayMs` of silence.
 *   - `flush()` fires whatever is queued and awaits it. Wire this
 *     to navigation `beforeRemove` and to `AppState.change` so a
 *     user who taps back or backgrounds the app inside the
 *     debounce window does not lose the edit.
 *   - `pending`, `error`, `succeededAt`, and `retry()` are reactive
 *     autosave UI state — bind them to a "Saving…" / "Saved" /
 *     "Save failed — Retry" indicator.
 *
 * Phase 6 (plan.md H1) — replaces a thin alias around the legacy
 * `useDebouncedCallback`, which silently dropped the pending timer
 * on unmount. The new hook keeps the trailing call alive across
 * unmount via `queueMicrotask` so React Query carries it to
 * completion.
 */
export function useDebouncedUpdateOrganization(delayMs = 1000) {
  const { updateOrganization } = useUpdateOrganization();
  return useDebouncedAsyncCallback(
    (vars: UpdateOrganizationVariables) => updateOrganization(vars),
    delayMs,
  );
}
